import { useState, useEffect, useMemo } from 'react';
import { useUpdateLedgerReturn, useDeleteLedgerReturn } from '@/api/hooks/useLedgerReturns';
import { InlineEditRow } from '@/components/InlineEditRow';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { ArrowDownIcon, ArrowUpIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import type { LedgerReturn, LedgerReturnType } from '@/api/types';
import { useCurrencyFractionDigits } from '@/hooks/useCurrencyFractionDigits';
import { decimalToMinorUnits, minorUnitsToDecimal } from '@/utils/money';

const TYPE_OPTIONS: { value: LedgerReturnType; label: string }[] = [
    { value: 'inflow', label: 'Inflow' },
    { value: 'outflow', label: 'Outflow' },
];

interface ReturnRowProps {
    /** The ledger return to display/edit. */
    returnItem: LedgerReturn;
    /** The ledger ID this return belongs to. */
    ledgerId: number;
    /** Ledger's confirmed updated date timestamp (null = never confirmed). */
    confirmedUpdatedDate: number | null;
    /** Whether this row is currently in edit mode. */
    isEditing: boolean;
    /** Callback when edit button is clicked. */
    onEdit: () => void;
    /** Callback when editing is cancelled. */
    onCancelEdit: () => void;
    /** Callback when save is complete. */
    onSaveComplete: () => void;
}

/**
 * Inline editable row for a ledger return.
 * View mode: date, type indicator (green/red), formatted amount, edit button.
 * Edit mode: date picker, type select, amount input, currency select, save/cancel/delete buttons.
 * Includes delete confirmation dialog to prevent accidental deletions.
 */
export function ReturnRow({
    returnItem,
    ledgerId,
    confirmedUpdatedDate,
    isEditing,
    onEdit,
    onCancelEdit,
    onSaveComplete,
}: ReturnRowProps) {
    const updateReturn = useUpdateLedgerReturn(ledgerId);
    const deleteReturn = useDeleteLedgerReturn(ledgerId);

    // Delete confirmation dialog state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Edit form state
    const [editType, setEditType] = useState<LedgerReturnType>('inflow');
    const [editDate, setEditDate] = useState('');
    const [editAmount, setEditAmount] = useState('');
    const [editCurrency, setEditCurrency] = useState('');
    const [editLabel, setEditLabel] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const originalFractionDigits = useCurrencyFractionDigits(returnItem.currency);
    const editFractionDigits = useCurrencyFractionDigits(editCurrency);

    // Initialize form values when entering edit mode
    useEffect(() => {
        if (isEditing) {
            // Use dateFormatted (Y-m-d format) for the date input
            setEditDate(returnItem.dateFormatted);
            setEditType(returnItem.type);
            setEditAmount(minorUnitsToDecimal(returnItem.value, originalFractionDigits));
            setEditCurrency(returnItem.currency);
            setEditLabel(returnItem.label ?? '');
            setErrors({});
        }
    }, [isEditing, originalFractionDigits, returnItem]);

    // Format the date for display
    const formattedDate = useMemo(() => {
        // Parse the ISO 8601 date string
        const date = new Date(returnItem.date);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }, [returnItem.date]);

    // Get Unix timestamp from ISO date string for comparison with confirmedUpdatedDate
    const returnDateTimestamp = useMemo(() => {
        return Math.floor(new Date(returnItem.date).getTime() / 1000);
    }, [returnItem.date]);

    const handleSave = async () => {
        setErrors({});

        // Validation
        const newErrors: Record<string, string> = {};
        if (!editDate) {
            newErrors.date = 'Date is required';
        }
        if (!editAmount.trim()) {
            newErrors.amount = 'Amount must be greater than 0';
        }
        if (!editCurrency) {
            newErrors.currency = 'Currency is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let newAmountInMinor: number;
        try {
            newAmountInMinor = decimalToMinorUnits(editAmount, editFractionDigits);
            if (newAmountInMinor <= 0) {
                throw new Error('Amount must be greater than 0');
            }
        } catch (error) {
            setErrors({ amount: error instanceof Error ? error.message : 'Invalid amount' });
            return;
        }

        try {
            // Convert date to ISO 8601 string
            const newDateISO = new Date(editDate + 'T00:00:00Z').toISOString();

            // Build update data - only include changed fields
            const updates: {
                date?: string;
                label?: string | null;
                type?: LedgerReturnType;
                value?: number;
                currency?: string;
            } = {};

            // Check what changed
            // Compare dates by their formatted string (Y-m-d)
            if (editDate !== returnItem.dateFormatted) {
                updates.date = newDateISO;
            }

            if (editLabel !== (returnItem.label ?? '')) {
                updates.label = editLabel || null;
            }

            if (editType !== returnItem.type) {
                updates.type = editType;
            }

            const currencyChanged = editCurrency !== returnItem.currency;
            if (newAmountInMinor !== returnItem.value || currencyChanged) {
                updates.value = newAmountInMinor;
            }

            if (currencyChanged) {
                updates.currency = editCurrency;
            }

            if (Object.keys(updates).length === 0) {
                // Nothing changed
                onCancelEdit();
                return;
            }

            await updateReturn.mutateAsync({
                returnId: returnItem.id,
                data: updates,
            });

            toast.success('Cash flow updated');
            onSaveComplete();
        } catch (error) {
            const isTransient = error instanceof ApiError && error.isTransient;
            toast.error(`Failed to update cash flow.${isTransient ? ' Please try again.' : ''}`);
            console.error('Failed to update return:', error);
        }
    };

    const handleDeleteClick = () => {
        // Show confirmation dialog
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteReturn.mutateAsync(returnItem.id);
            toast.success('Cash flow deleted');
            setShowDeleteConfirm(false);
            onSaveComplete();
        } catch (error) {
            const isTransient = error instanceof ApiError && error.isTransient;
            toast.error(`Failed to delete cash flow.${isTransient ? ' Please try again.' : ''}`);
            console.error('Failed to delete return:', error);
            setShowDeleteConfirm(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    // View content with visual distinction for inflows/outflows.
    const isInflow = returnItem.type === 'inflow';
    const typeLabel = isInflow ? 'inflow' : 'outflow';

    // Check if return date is ahead of confirmed date (excluded from analytics).
    // Normalize both to midnight (seconds since epoch for start of day) for date-only comparison.
    const SECONDS_PER_DAY = 86400;
    const returnDateMidnight = Math.floor(returnDateTimestamp / SECONDS_PER_DAY) * SECONDS_PER_DAY;
    const confirmedMidnight = confirmedUpdatedDate !== null
        ? Math.floor(confirmedUpdatedDate / SECONDS_PER_DAY) * SECONDS_PER_DAY
        : null;
    const isExcludedFromAnalytics =
        confirmedMidnight === null || returnDateMidnight > confirmedMidnight;

    const viewContent = (
        <div className="flex items-center justify-between w-full gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <span
                    className={`inline-flex items-center justify-center gap-1 w-12 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                        isInflow
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                >
                    {isInflow ? (
                        <ArrowDownIcon className="h-3 w-3" />
                    ) : (
                        <ArrowUpIcon className="h-3 w-3" />
                    )}
                    {isInflow ? 'In' : 'Out'}
                </span>
                <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap shrink-0">{formattedDate}</span>
                {isExcludedFromAnalytics && (
                    <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 shrink-0"
                        title="Not included in analytics (ahead of latest confirmed date)"
                    >
                        <EyeSlashIcon className="h-3 w-3" />
                    </span>
                )}
                {returnItem.label && (
                    <span
                        className="text-sm text-gray-500 dark:text-gray-400 truncate"
                        title={returnItem.label}
                    >
                        {returnItem.label}
                    </span>
                )}
            </div>
            <MoneyDisplay
                value={returnItem.valueDecimal}
                currency={returnItem.currency}
                fractionDigits={originalFractionDigits}
                size="md"
                className="font-medium shrink-0 text-gray-900 dark:text-white"
            />
        </div>
    );

    // Edit content
    const editContent = (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Select
                    label="Type"
                    value={editType}
                    onChange={(val) => setEditType(val as LedgerReturnType)}
                    options={TYPE_OPTIONS}
                />
                <Input
                    label="Date"
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    error={errors.date}
                    required
                />
                <MoneyInput
                    label="Amount"
                    value={editAmount}
                    currency={editCurrency}
                    onValueChange={setEditAmount}
                    onCurrencyChange={setEditCurrency}
                    error={errors.amount || errors.currency}
                    placeholder="0.00"
                />
            </div>
            <Input
                label="Label (optional)"
                type="text"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                placeholder="e.g., Dividend, Withdrawal"
            />
        </div>
    );

    return (
        <>
            <InlineEditRow
                isEditing={isEditing}
                onEdit={onEdit}
                onSave={handleSave}
                onCancel={onCancelEdit}
                onDelete={handleDeleteClick}
                viewContent={viewContent}
                editContent={editContent}
                editTitle="Edit cash flow"
                isSaving={updateReturn.isPending}
                isDeleting={deleteReturn.isPending}
            />

            <DeleteConfirmDialog
                open={showDeleteConfirm}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Cash Flow"
                description={`Are you sure you want to delete the ${typeLabel} from ${formattedDate}${returnItem.label ? ` (${returnItem.label})` : ''}? This action cannot be undone.`}
                confirmLabel="Delete"
                isLoading={deleteReturn.isPending}
            />
        </>
    );
}
